import { replaceLinksWithAnchorTags } from 'src/utils/strings';

const HTML_TAG_NAME_PATTERN = '[A-Za-z][A-Za-z0-9:-]*';
const HTML_UNQUOTED_ATTRIBUTE_VALUE_PATTERN = `[^\\s"'=<>]+`;
const HTML_ATTRIBUTE_PATTERN = `(?:\\s+[^\\s"'<>/=]+(?:\\s*=\\s*(?:"[^"]*"|'[^']*'|${HTML_UNQUOTED_ATTRIBUTE_VALUE_PATTERN}))?)*\\s*`;
const RAW_HTML_TAG_SOURCE = `</?${HTML_TAG_NAME_PATTERN}${HTML_ATTRIBUTE_PATTERN}/?>`;
const RAW_HTML_TAG_PATTERN = new RegExp(RAW_HTML_TAG_SOURCE, 'i');
const ESCAPED_HTML_TAG_PATTERN = new RegExp(`&lt;/?${HTML_TAG_NAME_PATTERN}(?:\\s[\\s\\S]*?)?/?&gt;`, 'i');
const RAW_HTML_TAG_GLOBAL_PATTERN = new RegExp(RAW_HTML_TAG_SOURCE, 'gi');

function decodeHtmlEntities(value: string) {
  if (typeof document === 'undefined') {
    return value;
  }

  const textarea = document.createElement('textarea');
  textarea.innerHTML = value;
  return textarea.value;
}

function normalizeWhitespace(value: string) {
  return value
    .replaceAll('\u00a0', ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

function escapeHtml(value: string) {
  return value
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function stripHtmlPreservingBreaks(value: string) {
  const htmlWithBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(?:blockquote|div|h[1-6]|li|ol|p|pre|table|tbody|td|tfoot|th|thead|tr|ul)>/gi, '\n')
    .replace(/<(?:li)\b[^>]*>/gi, '• ');

  if (typeof document === 'undefined') {
    return normalizeWhitespace(htmlWithBreaks.replace(RAW_HTML_TAG_GLOBAL_PATTERN, ''));
  }

  const container = document.createElement('div');
  container.innerHTML = htmlWithBreaks;

  return normalizeWhitespace(container.textContent ?? '');
}

// Defensive renderer-only workaround for activity log comments stored as HTML instead of canonical plaintext/markdown.
export function sanitizeActivityLogCommentText(value: string) {
  const trimmedValue = value.trim();
  if (!trimmedValue) {
    return value;
  }

  const containsRawHtml = RAW_HTML_TAG_PATTERN.test(trimmedValue);
  const containsEscapedHtml = ESCAPED_HTML_TAG_PATTERN.test(trimmedValue);
  if (!containsRawHtml && !containsEscapedHtml) {
    return value;
  }

  const decodedValue = containsEscapedHtml && !containsRawHtml ? decodeHtmlEntities(trimmedValue) : trimmedValue;
  if (!RAW_HTML_TAG_PATTERN.test(decodedValue)) {
    return value;
  }

  return stripHtmlPreservingBreaks(decodedValue);
}

export function normalizeActivityLogLegacyText(value: string) {
  return sanitizeActivityLogCommentText(value);
}

export function buildActivityLogLegacyTextHtml(value: string): { hasLink: boolean; text: string } {
  return replaceLinksWithAnchorTags(escapeHtml(normalizeActivityLogLegacyText(value)));
}
